import React from 'react';
import { useParams } from 'react-router-dom';
import { useAccessGroups } from './ResourcesRouter.js';
import {
	Card,
	CardBody,
	CardHeader,
	Button,
	Input,
	Skeleton,
	Divider,
	addToast
} from '@heroui/react';
import { BarChart3, Save, Plus, Trash2, Search, X } from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';
import { UnsavedChangesGuard, useUnsavedChangesGuard } from './components/UnsavedChangesGuard.js';
import EnvelopeChart from '../common/EnvelopeChart.js';

// Get organization unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

// Empty limit row template
const emptyLimitRow = () => ({
	minWeight: '',
	minWeightCgLimit: '',
	maxWeight: '',
	maxWeightCgLimit: ''
});

// Max decimal places for envelope values
const MAX_DECIMALS = 4;

// Validate and format decimal input (max 4 decimal places)
const formatDecimalInput = value => {
	if (!value) return value;
	const str = String(value);
	const dotIndex = str.indexOf('.');
	if (dotIndex === -1) return str;
	return str.slice(0, dotIndex + MAX_DECIMALS + 1);
};

function EnvelopePageContent() {
	const { aircraftId } = useParams();
	const accessGroups = useAccessGroups();
	const canManage = accessGroups._loaded && !!accessGroups.resourcesManage;
	const orgUnique = getOrgUnique();
	const { setDirty, navigateWithCheck } = useUnsavedChangesGuard();

	const [aircraft, setAircraft] = React.useState(null);
	const [loading, setLoading] = React.useState(true);
	const [saving, setSaving] = React.useState(false);

	// CG Envelope form state
	const [forwardLimits, setForwardLimits] = React.useState([]);
	const [aftLimits, setAftLimits] = React.useState([]);
	const [unitWeightCG, setUnitWeightCG] = React.useState('lbs');
	const [unitForwardAftCG, setUnitForwardAftCG] = React.useState('in');

	// Search state
	const [searchQuery, setSearchQuery] = React.useState('');
	const [searchResults, setSearchResults] = React.useState([]);
	const [searching, setSearching] = React.useState(false);

	// Track initial data for dirty checking
	const initialDataRef = React.useRef(null);

	// Check if data has changed from initial values
	const checkDirty = React.useCallback((fwd, aft) => {
		if (!initialDataRef.current) return false;
		const initial = initialDataRef.current;
		return (
			JSON.stringify(fwd) !== JSON.stringify(initial.forwardLimits) ||
			JSON.stringify(aft) !== JSON.stringify(initial.aftLimits)
		);
	}, []);

	// Fetch aircraft and envelope data
	const fetchData = React.useCallback(async () => {
		if (!aircraftId) return;
		setLoading(true);
		try {
			const [aircraftRes, envelopeRes] = await Promise.all([
				ResourcesApi.getAircraft(aircraftId),
				ResourcesApi.getEnvelope(aircraftId)
			]);

			const aircraftData = aircraftRes.data?.data || aircraftRes.data || aircraftRes;
			setAircraft(aircraftData);

			const envelopeData = envelopeRes.data?.data || envelopeRes.data || envelopeRes;
			const cgLimit = envelopeData.envelopCgLimit || {};
			const fwd = cgLimit.forward_cg_limit || cgLimit.forwardCgLimit || [emptyLimitRow()];
			const aft = cgLimit.aft_cg_limit || cgLimit.aftCgLimit || [emptyLimitRow()];
			setForwardLimits(fwd);
			setAftLimits(aft);
			// Store initial values for dirty checking
			initialDataRef.current = {
				forwardLimits: JSON.parse(JSON.stringify(fwd)),
				aftLimits: JSON.parse(JSON.stringify(aft))
			};
			setDirty(false);
			// Load units from API or use defaults
			setUnitWeightCG(envelopeData.unitWeightCG || 'lbs');
			setUnitForwardAftCG(envelopeData.unitForwardAftCG || 'in');
		} catch (err) {
			console.error('Failed to load data:', err);
			setForwardLimits([emptyLimitRow()]);
			setAftLimits([emptyLimitRow()]);
		} finally {
			setLoading(false);
		}
	}, [aircraftId]);

	React.useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Handle forward limit changes
	const handleForwardChange = (index, field, value) => {
		const formattedValue = formatDecimalInput(value);
		setForwardLimits(prev => {
			const newLimits = [...prev];
			newLimits[index] = { ...newLimits[index], [field]: formattedValue };
			setDirty(checkDirty(newLimits, aftLimits));
			return newLimits;
		});
	};

	// Handle aft limit changes
	const handleAftChange = (index, field, value) => {
		const formattedValue = formatDecimalInput(value);
		setAftLimits(prev => {
			const newLimits = [...prev];
			newLimits[index] = { ...newLimits[index], [field]: formattedValue };
			setDirty(checkDirty(forwardLimits, newLimits));
			return newLimits;
		});
	};

	// Add/remove rows
	const handleAddForwardRow = () => {
		setForwardLimits(prev => {
			const newLimits = [...prev, emptyLimitRow()];
			setDirty(checkDirty(newLimits, aftLimits));
			return newLimits;
		});
	};
	const handleRemoveForwardRow = index => {
		setForwardLimits(prev => {
			const newLimits = prev.filter((_, i) => i !== index);
			setDirty(checkDirty(newLimits, aftLimits));
			return newLimits;
		});
	};
	const handleAddAftRow = () => {
		setAftLimits(prev => {
			const newLimits = [...prev, emptyLimitRow()];
			setDirty(checkDirty(forwardLimits, newLimits));
			return newLimits;
		});
	};
	const handleRemoveAftRow = index => {
		setAftLimits(prev => {
			const newLimits = prev.filter((_, i) => i !== index);
			setDirty(checkDirty(forwardLimits, newLimits));
			return newLimits;
		});
	};

	// Search for envelope templates
	const handleSearch = async () => {
		if (searchQuery.length < 3) {
			addToast({
				title: 'Info',
				description: 'Enter at least 3 characters to search',
				color: 'warning'
			});
			return;
		}

		setSearching(true);
		try {
			const response = await ResourcesApi.searchAircraftModels(searchQuery);
			const responseData = response.data?.data || response.data || {};
			// API returns { items: [...], hasMore: bool }
			const results = responseData.items || responseData || [];
			setSearchResults(Array.isArray(results) ? results : []);
			if (results.length === 0) {
				addToast({
					title: 'No Results',
					description: 'No aircraft models found matching your search',
					color: 'default'
				});
			}
		} catch (err) {
			console.error('Search failed:', err);
			addToast({
				title: 'Search',
				description: 'Search feature requires API support. Enter envelope data manually.',
				color: 'default'
			});
			setSearchResults([]);
		} finally {
			setSearching(false);
		}
	};

	// Apply envelope from search result
	const handleApplyEnvelope = result => {
		// envelopCgLimit may be a JSON string from API - parse if needed
		let cgLimit = result.envelopCgLimit || result.cgLimit;

		if (typeof cgLimit === 'string') {
			try {
				cgLimit = JSON.parse(cgLimit);
			} catch {
				cgLimit = null;
			}
		}

		// Handle different API response formats:
		// Format 1: { forward_cg_limit: [...], aft_cg_limit: [...] }
		// Format 2: [{ weight_cg, forward_cg, aft_cg }, ...] - array of points
		let forward = [];
		let aft = [];

		if (Array.isArray(cgLimit) && cgLimit.length > 0) {
			// Format 2: Array of points with weight_cg, forward_cg, aft_cg
			// Use Douglas-Peucker algorithm to simplify to key vertices
			const douglasPeucker = (points, getX, getY, epsilon) => {
				if (points.length <= 2) return points;

				// Find point with max distance from line between first and last
				const first = points[0];
				const last = points[points.length - 1];
				const x1 = parseFloat(getX(first));
				const y1 = parseFloat(getY(first));
				const x2 = parseFloat(getX(last));
				const y2 = parseFloat(getY(last));

				let maxDist = 0;
				let maxIndex = 0;

				for (let i = 1; i < points.length - 1; i++) {
					const x0 = parseFloat(getX(points[i]));
					const y0 = parseFloat(getY(points[i]));

					// Perpendicular distance from point to line
					const num = Math.abs((y2 - y1) * x0 - (x2 - x1) * y0 + x2 * y1 - y2 * x1);
					const den = Math.sqrt((y2 - y1) ** 2 + (x2 - x1) ** 2);
					const dist = den > 0 ? num / den : 0;

					if (dist > maxDist) {
						maxDist = dist;
						maxIndex = i;
					}
				}

				// If max distance > epsilon, recursively simplify
				if (maxDist > epsilon) {
					const left = douglasPeucker(points.slice(0, maxIndex + 1), getX, getY, epsilon);
					const right = douglasPeucker(points.slice(maxIndex), getX, getY, epsilon);
					return [...left.slice(0, -1), ...right];
				}

				return [first, last];
			};

			// Simplify with epsilon = 0.5 (tolerance in CG units)
			const forwardPoints = douglasPeucker(
				cgLimit,
				p => p.weight_cg,
				p => p.forward_cg,
				0.5
			);
			const aftPoints = douglasPeucker(
				cgLimit,
				p => p.weight_cg,
				p => p.aft_cg,
				0.5
			);

			// Convert to segments
			for (let i = 0; i < forwardPoints.length - 1; i++) {
				const current = forwardPoints[i];
				const next = forwardPoints[i + 1];
				forward.push({
					minWeight: current.weight_cg,
					minWeightCgLimit: current.forward_cg,
					maxWeight: next.weight_cg,
					maxWeightCgLimit: next.forward_cg
				});
			}

			for (let i = 0; i < aftPoints.length - 1; i++) {
				const current = aftPoints[i];
				const next = aftPoints[i + 1];
				aft.push({
					minWeight: current.weight_cg,
					minWeightCgLimit: current.aft_cg,
					maxWeight: next.weight_cg,
					maxWeightCgLimit: next.aft_cg
				});
			}
		} else if (cgLimit && typeof cgLimit === 'object') {
			// Format 1: Object with forward_cg_limit and aft_cg_limit arrays
			forward = cgLimit.forward_cg_limit || cgLimit.forwardCgLimit || [];
			aft = cgLimit.aft_cg_limit || cgLimit.aftCgLimit || [];
		}

		if (forward.length === 0 && aft.length === 0) {
			addToast({
				title: 'No Envelope Data',
				description: `${result.manufacturer || ''} ${result.model || ''} doesn't have envelope limits defined`,
				color: 'warning'
			});
			return;
		}

		const newForward = forward.length > 0 ? forward : [emptyLimitRow()];
		const newAft = aft.length > 0 ? aft : [emptyLimitRow()];
		setForwardLimits(newForward);
		setAftLimits(newAft);
		setDirty(checkDirty(newForward, newAft));
		setSearchResults([]);
		setSearchQuery('');
		addToast({
			title: 'Envelope Applied',
			description: `Applied envelope from ${result.manufacturer || ''} ${result.model || ''}`,
			color: 'success'
		});
	};

	// Save envelope
	const handleSave = async () => {
		setSaving(true);
		try {
			// Backend DTO expects string values, not numbers
			const toNumericString = value => {
				const num = parseFloat(value);
				return isNaN(num) ? '0' : String(num);
			};

			const payload = {
				unitWeightCG,
				unitForwardAftCG,
				envelopCgLimit: {
					forward_cg_limit: forwardLimits.map(limit => ({
						minWeight: toNumericString(limit.minWeight),
						minWeightCgLimit: toNumericString(limit.minWeightCgLimit),
						maxWeight: toNumericString(limit.maxWeight),
						maxWeightCgLimit: toNumericString(limit.maxWeightCgLimit)
					})),
					aft_cg_limit: aftLimits.map(limit => ({
						minWeight: toNumericString(limit.minWeight),
						minWeightCgLimit: toNumericString(limit.minWeightCgLimit),
						maxWeight: toNumericString(limit.maxWeight),
						maxWeightCgLimit: toNumericString(limit.maxWeightCgLimit)
					}))
				}
			};

			await ResourcesApi.saveEnvelope(parseInt(aircraftId, 10), payload);
			// Update initial data and reset dirty state after successful save
			initialDataRef.current = {
				forwardLimits: JSON.parse(JSON.stringify(forwardLimits)),
				aftLimits: JSON.parse(JSON.stringify(aftLimits))
			};
			setDirty(false);
			addToast({
				title: 'Success',
				description: 'CG Envelope saved successfully',
				color: 'success'
			});
		} catch (err) {
			console.error('Failed to save:', err);
			const status = err.response?.status;
			const description =
				status === 422
					? 'Please fill in all required fields before saving'
					: 'Failed to save CG Envelope';
			addToast({ title: 'Error', description, color: 'danger' });
		} finally {
			setSaving(false);
		}
	};

	// Back navigation
	const handleBack = () => navigateWithCheck(`/aircraft/${aircraftId}/weight-balance`);

	// Handle search on Enter key
	const handleSearchKeyDown = e => {
		if (e.key === 'Enter') handleSearch();
	};

	// Clear search query and results
	const handleClearSearch = () => {
		setSearchQuery('');
		setSearchResults([]);
	};

	// Breadcrumbs
	const breadcrumbCustomItems = [
		{
			label: window.location.pathname.includes('/hangars') ? 'Aircraft Status' : 'Resources',
			href: `/org/${orgUnique}/${window.location.pathname.includes('/hangars') ? 'hangars' : 'resources'}/`
		},
		{
			label: aircraft?.name || 'Aircraft',
			href: `/org/${orgUnique}/resources/aircraft/${aircraftId}`
		},
		{
			label: 'Weight & Balance',
			href: `/org/${orgUnique}/resources/aircraft/${aircraftId}/weight-balance`
		}
	];

	// Render limit section
	const renderLimitSection = (title, limits, onChange, onAdd, onRemove, colorClass) => (
		<div>
			<div className="flex items-center justify-between mb-3">
				<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
					<span className={`w-1 h-4 ${colorClass} rounded-full`} />
					{title}
				</h4>
				{canManage && (
					<Button
						size="sm"
						variant="flat"
						color="success"
						startContent={<Plus className="w-3 h-3" />}
						onPress={onAdd}
						className="h-7"
					>
						Add
					</Button>
				)}
			</div>
			<div className="space-y-3">
				{limits.map((limit, index) => (
					<div key={index} className="p-3 rounded-lg bg-default-50 border border-default-100">
						<div className="flex items-center justify-between mb-3">
							<span className="text-xs font-semibold text-default-600">Line {index + 1}</span>
							{canManage && (
								<Button
									isIconOnly
									size="sm"
									variant="light"
									color="danger"
									onPress={() => onRemove(index)}
									isDisabled={limits.length === 1}
									className="h-7 w-7 min-w-7"
								>
									<Trash2 className="w-4 h-4" />
								</Button>
							)}
						</div>
						<div className="grid grid-cols-2 gap-3">
							<Input
								size="sm"
								type="number"
								label="Min Weight"
								labelPlacement="outside"
								placeholder="0"
								value={limit.minWeight}
								isReadOnly={!canManage}
								onChange={e => onChange(index, 'minWeight', e.target.value)}
							/>
							<Input
								size="sm"
								type="number"
								step="0.01"
								label="Min CG"
								labelPlacement="outside"
								placeholder="0.00"
								value={limit.minWeightCgLimit}
								isReadOnly={!canManage}
								onChange={e => onChange(index, 'minWeightCgLimit', e.target.value)}
							/>
							<Input
								size="sm"
								type="number"
								label="Max Weight"
								labelPlacement="outside"
								placeholder="0"
								value={limit.maxWeight}
								isReadOnly={!canManage}
								onChange={e => onChange(index, 'maxWeight', e.target.value)}
							/>
							<Input
								size="sm"
								type="number"
								step="0.01"
								label="Max CG"
								labelPlacement="outside"
								placeholder="0.00"
								value={limit.maxWeightCgLimit}
								isReadOnly={!canManage}
								onChange={e => onChange(index, 'maxWeightCgLimit', e.target.value)}
							/>
						</div>
					</div>
				))}
			</div>
		</div>
	);

	// Loading skeleton
	if (loading) {
		return (
			<div className="max-w-[1400px] mx-auto px-4 py-8">
				<Skeleton className="h-8 w-48 rounded-lg mb-6" />
				<div className="flex items-center gap-4 mb-8">
					<Skeleton className="w-14 h-14 rounded-full" />
					<div>
						<Skeleton className="w-48 h-7 rounded-lg mb-2" />
						<Skeleton className="w-64 h-5 rounded-lg" />
					</div>
				</div>
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<Skeleton className="h-[500px] w-full rounded-xl" />
					<Skeleton className="h-[500px] w-full rounded-xl" />
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-8">
			<HeroUIBreadcrumb currentPage="W&B Envelope" customItems={breadcrumbCustomItems} />

			{/* Page Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
				<div className="flex items-center gap-4">
					<div className="p-3 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600">
						<BarChart3 className="w-8 h-8 text-white" />
					</div>
					<div>
						<h1 className="text-2xl font-bold">{aircraft?.name || 'Aircraft'}</h1>
						<p className="text-default-500 text-sm">
							{aircraft?.registrationNumber}
							{(aircraft?.manufacturer || aircraft?.model) && ' · '}
							{[aircraft?.manufacturer, aircraft?.model].filter(Boolean).join(' ')}
							{' · W&B Envelope'}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-3 self-end md:self-auto">
					{canManage && (
						<Button
							color="success"
							startContent={<Save className="w-4 h-4" />}
							onPress={handleSave}
							isLoading={saving}
						>
							Save Envelope
						</Button>
					)}
					<Button variant="light" isIconOnly onPress={handleBack}>
						<X className="w-5 h-5" />
					</Button>
				</div>
			</div>

			{/* Main Content - 2 Columns */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
				{/* Left Column - Chart */}
				<Card className="border border-default-200 shadow-md bg-gradient-to-br from-white to-indigo-50/30">
					<CardHeader className="bg-gradient-to-r from-indigo-100/80 via-indigo-50/50 to-transparent pb-3 border-b border-indigo-100/50">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-indigo-500/10 ring-1 ring-indigo-500/20">
								<BarChart3 className="w-5 h-5 text-indigo-600" />
							</div>
							<span className="text-lg font-semibold text-indigo-900">Envelope Preview</span>
						</div>
					</CardHeader>
					<CardBody className="pt-4">
						<EnvelopeChart
							forwardLimits={forwardLimits}
							aftLimits={aftLimits}
							fuelCasePoints={null}
						/>
					</CardBody>
				</Card>

				{/* Right Column - Data Entry */}
				<Card className="border border-default-200 shadow-md bg-gradient-to-br from-white to-purple-50/30">
					<CardHeader className="bg-gradient-to-r from-purple-100/80 via-purple-50/50 to-transparent pb-3 border-b border-purple-100/50">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-purple-500/10 ring-1 ring-purple-500/20">
								<Search className="w-5 h-5 text-purple-600" />
							</div>
							<span className="text-lg font-semibold text-purple-900">CG Limits</span>
						</div>
					</CardHeader>
					<CardBody className="pt-4">
						{/* Search Section (canManage only) */}
						{canManage && (
							<div className="mb-4">
								<div className="mb-5">
									<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
										<span className="w-1 h-4 bg-purple-400 rounded-full" />
										Find Template
									</h4>
								</div>
								<div className="flex gap-2">
									<Input
										size="sm"
										placeholder="Search by model or manufacturer..."
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
										onKeyDown={handleSearchKeyDown}
										className="flex-1"
										endContent={
											searchQuery && (
												<button
													type="button"
													onClick={handleClearSearch}
													className="p-1 rounded-full hover:bg-default-200 transition-colors"
												>
													<X className="w-3 h-3 text-default-400" />
												</button>
											)
										}
									/>
									<Button
										size="sm"
										color="primary"
										onPress={handleSearch}
										isLoading={searching}
										startContent={!searching && <Search className="w-4 h-4" />}
									>
										Find
									</Button>
								</div>

								{/* Search Results */}
								{searchResults.length > 0 && (
									<div className="mt-3">
										<div className="mb-2">
											<span className="text-xs text-default-500">
												{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
											</span>
										</div>
										<div className="space-y-2">
											{searchResults.map((result, index) => (
												<div
													key={index}
													className="flex justify-between items-center p-2 rounded-lg bg-default-100 hover:bg-default-200 transition-colors"
												>
													<span className="text-sm">
														{result.manufacturer} {result.model} {result.popularName || ''}
													</span>
													{canManage && (
														<Button
															size="sm"
															color="primary"
															variant="flat"
															onPress={() => handleApplyEnvelope(result)}
														>
															Apply
														</Button>
													)}
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						)}

						<Divider className="my-4" />

						{/* Forward Limits */}
						{renderLimitSection(
							'Forward Limits',
							forwardLimits,
							handleForwardChange,
							handleAddForwardRow,
							handleRemoveForwardRow,
							'bg-blue-400'
						)}

						<Divider className="my-4" />

						{/* Aft Limits */}
						{renderLimitSection(
							'Aft Limits',
							aftLimits,
							handleAftChange,
							handleAddAftRow,
							handleRemoveAftRow,
							'bg-amber-400'
						)}
					</CardBody>
				</Card>
			</div>
		</div>
	);
}

// Wrap with UnsavedChangesGuard
function EnvelopePage() {
	return (
		<UnsavedChangesGuard>
			<EnvelopePageContent />
		</UnsavedChangesGuard>
	);
}

export default EnvelopePage;
